import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import Dropdown, { DropdownOption } from 'v3/Components/Form/Dropdown';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { buildEndpoint, useApiWorker, useDataPoster } from 'Utilities';
import { useToasts } from 'Context';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';

// Reviewer type values mirror backend ReviewerType enumeration
const REVIEWER_TYPE_PEER_REVIEW = 1;
const REVIEWER_TYPE_IME = 2;
const REVIEWER_TYPE_PEER_REVIEW_IME = 3;

const REVIEWER_TYPE_OPTIONS: DropdownOption[] = [
  { value: REVIEWER_TYPE_PEER_REVIEW, displayName: 'Peer Review' },
  { value: REVIEWER_TYPE_IME, displayName: 'IME' },
  { value: REVIEWER_TYPE_PEER_REVIEW_IME, displayName: 'Peer Review, IME' },
];

const COSIGNER_OPTIONS: DropdownOption[] = [
  { value: 'true', displayName: 'Yes' },
  { value: 'false', displayName: 'No' },
];

export type FinancialDocument = {
  id: string;
  title: string;
  fileName: string;
};

export type RatesData = {
  reviewerTypeValue?: number;
  reviewerTypeDisplayName?: string;
  isCosigner?: boolean | null;
  perCaseRate?: number | null;
  hourlyRate?: number | null;
  urPaRate?: number | null;
  cosignRate?: number | null;
  recentFinancialDocuments?: {
    feeSchedule?: FinancialDocument;
    contract?: FinancialDocument;
    addendum?: FinancialDocument;
  };
};

type Props = {
  data: RatesData;
  reviewerId?: string;
  onCancel: () => void;
  onSaveSuccess: () => void;
};

type ReadOnlyProps = {
  data: RatesData;
  reviewerId?: string;
};

const isPeerReviewType = (typeValue?: number) =>
  typeValue === REVIEWER_TYPE_PEER_REVIEW || typeValue === REVIEWER_TYPE_PEER_REVIEW_IME;

const hasIMEType = (typeValue?: number) =>
  typeValue === REVIEWER_TYPE_IME || typeValue === REVIEWER_TYPE_PEER_REVIEW_IME;

const formatCurrency = (value?: number | null) => {
  if (value == null) return '—';
  return `$${value % 1 === 0 ? value : value.toFixed(2)}`;
};

const RateTile: React.FC<{ label: string; value?: number | null }> = ({ label, value }) => (
  <div className="overview-rate-tile">
    <span className="overview-rate-tile__value">{formatCurrency(value)}</span>
    <span className="overview-rate-tile__label">{label}</span>
  </div>
);

const FinancialDocumentRow: React.FC<{
  label: string;
  required?: boolean;
  document?: FinancialDocument;
  onDownload?: () => void;
}> = ({ label, required, document, onDownload }) => (
  <div className="overview-financial-doc-row">
    <span className="overview-financial-doc-row__name">
      {label}
      {required && <span className="overview-financial-doc-row__required"> *</span>}
    </span>
    {document && (
      <div className="overview-financial-doc-row__file">
        <DescriptionOutlinedIcon
          className="overview-financial-doc-row__file-icon"
        />
        <button
          type="button"
          className="overview-financial-doc-row__link"
          onClick={onDownload}
        >
          {document.title}
        </button>
        <button
          type="button"
          className="overview-financial-doc-row__download-btn"
          onClick={onDownload}
          aria-label={`Download ${document.title}`}
        >
          <FileDownloadOutlinedIcon />
        </button>
      </div>
    )}
  </div>
);

const useDocumentDownload = (reviewerId?: string) => {
  const API = useApiWorker('3.0');

  return async (doc: FinancialDocument) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.reviewers.base,
      reviewerId,
      'documents',
      doc.id,
      'download'
    );
    const response = await API.get(endpoint, { responseType: 'blob' });
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName || doc.title || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
};

export const RatesReadOnly: React.FC<ReadOnlyProps> = ({ data, reviewerId }) => {
  const showPeerReviewFields = isPeerReviewType(data.reviewerTypeValue);
  const showFeeSchedule = hasIMEType(data.reviewerTypeValue);
  const handleDownload = useDocumentDownload(reviewerId);
  const docs = data.recentFinancialDocuments;

  return (
    <div>
      <p className="overview-subsection-label">Type</p>
      <ReadOnlyField forceLabel="Reviewer Type" value={data.reviewerTypeDisplayName} />
      {showPeerReviewFields && (
        <ReadOnlyField
          forceLabel="Cosigner"
          value={data.isCosigner == null ? undefined : data.isCosigner ? 'Yes' : 'No'}
        />
      )}

      {showPeerReviewFields && (
        <>
          <p className="overview-subsection-label" style={{ marginTop: 24 }}>Rate Breakdown</p>
          <div className="overview-rate-breakdown">
            <RateTile label="Per Case Rate" value={data.perCaseRate} />
            <RateTile label="Hourly Rate" value={data.hourlyRate} />
            <RateTile label="UR/PA Rate" value={data.urPaRate} />
            {data.isCosigner !== false && (
              <RateTile label="Cosign Rate" value={data.cosignRate} />
            )}
          </div>
        </>
      )}

      <p className="overview-subsection-label" style={{ marginTop: 24 }}>Recent Financial Documents</p>
      <div className="overview-financial-docs">
        {showFeeSchedule && (
          <FinancialDocumentRow
            label="Fee Schedule"
            required
            document={docs?.feeSchedule}
            onDownload={docs?.feeSchedule ? () => handleDownload(docs.feeSchedule!) : undefined}
          />
        )}
        <FinancialDocumentRow
          label="Contract"
          required
          document={docs?.contract}
          onDownload={docs?.contract ? () => handleDownload(docs.contract!) : undefined}
        />
        <FinancialDocumentRow
          label="Addendum"
          document={docs?.addendum}
          onDownload={docs?.addendum ? () => handleDownload(docs.addendum!) : undefined}
        />
      </div>
    </div>
  );
};

const CurrencyInput: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ label, value, onChange }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow empty, digits, and at most one decimal point
    // Reject non-numeric chars; allow trailing decimal so "150." doesn't nullify the field
    if (e.target.value === '' || /^\d{0,16}(\.\d{0,2})?$/.test(e.target.value)) {
      onChange(e);
    }
  };

  return (
    <div className="overview-rates-currency-input">
      <label className="overview-rates-currency-input__label">{label}</label>
      <div className="overview-rates-currency-input__wrapper">
        <span className="overview-rates-currency-input__symbol">$</span>
        <input
          className="overview-rates-currency-input__field"
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

type FormValues = {
  reviewerType: DropdownOption;
  cosigner: string;
  perCaseRate: string;
  hourlyRate: string;
  urPaRate: string;
  cosignRate: string;
};

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

const toReviewerTypeOption = (value?: number): DropdownOption =>
  REVIEWER_TYPE_OPTIONS.find((o) => o.value === value) ?? EMPTY_OPTION;

const toCosignerValue = (value?: boolean | null): string => {
  if (value == null) return '';
  return value ? 'true' : 'false';
};

const toRateString = (value?: number | null) => (value != null ? String(value) : '');

const validationSchema = Yup.object({
  reviewerType: Yup.object({
    value: Yup.mixed().required('Reviewer Type is required'),
  }),
  cosigner: Yup.string().when('reviewerType', {
    is: (reviewerType: DropdownOption) => isPeerReviewType(Number(reviewerType?.value)),
    then: Yup.string().required('Cosigner selection is required'),
  }),
});

export const RatesActive: React.FC<Props> = ({ data, reviewerId, onCancel, onSaveSuccess }) => {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const handleDownload = useDocumentDownload(reviewerId);
  const docs = data.recentFinancialDocuments;

  const toast = useToasts();

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'rates'),
    undefined,
    true,
    '3.0'
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      reviewerType: toReviewerTypeOption(data.reviewerTypeValue),
      cosigner: toCosignerValue(data.isCosigner),
      perCaseRate: toRateString(data.perCaseRate),
      hourlyRate: toRateString(data.hourlyRate),
      urPaRate: toRateString(data.urPaRate),
      cosignRate: toRateString(data.cosignRate),
    },
    validationSchema,
    onSubmit: async (values) => {
      const typeValue = Number(values.reviewerType.value);
      const peerReview = isPeerReviewType(typeValue);

      const resp: any = await postData({
        reviewerTypeValue: typeValue,
        isCosigner: peerReview
          ? values.cosigner === 'true'
            ? true
            : values.cosigner === 'false'
            ? false
            : null
          : null,
        perCaseRate: peerReview && values.perCaseRate ? parseFloat(values.perCaseRate) : null,
        hourlyRate: peerReview && values.hourlyRate ? parseFloat(values.hourlyRate) : null,
        urPaRate: peerReview && values.urPaRate ? parseFloat(values.urPaRate) : null,
        cosignRate: peerReview && values.cosignRate ? parseFloat(values.cosignRate) : null,
      });

      if (resp?.status === 200) {
        toast.success('Rates have been updated');
        onSaveSuccess();
        return;
      }

      // Covers network failures too (no response), which leave the card in edit mode untouched.
      const err = resp as AxiosError<any>;
      const errors: Record<string, string[]> =
        err?.response?.status === 400 ? err.response.data?.errors ?? {} : {};
      const messages = Object.values(errors).reduce<string[]>((acc, m) => acc.concat(m), []);
      if (messages.length > 0) {
        messages.forEach((msg) => toast.error(msg));
      } else {
        toast.error('Failed to save rates. Please try again.');
      }
    },
  });

  const selectedTypeValue = Number(formik.values.reviewerType.value);
  const showPeerReviewFields = isPeerReviewType(selectedTypeValue);
  const isCosignerYes = formik.values.cosigner === 'true';

  const handleCancel = () => {
    if (formik.dirty) {
      setConfirmDiscard(true);
    } else {
      onCancel();
    }
  };

  return (
    <div>
      <p className="overview-subsection-label">Type</p>

      <Dropdown
        label="Reviewer Type"
        value={formik.values.reviewerType}
        onChange={(opt) => {
          formik.setFieldValue('reviewerType', opt);
          // Clear peer-review-specific fields when switching to IME
          if (!isPeerReviewType(Number(opt.value))) {
            formik.setFieldValue('cosigner', '');
            formik.setFieldValue('perCaseRate', '');
            formik.setFieldValue('hourlyRate', '');
            formik.setFieldValue('urPaRate', '');
            formik.setFieldValue('cosignRate', '');
          }
        }}
        options={REVIEWER_TYPE_OPTIONS}
        required
        showIcons={false}
        searchable={false}
      />

      {showPeerReviewFields && (
        <div className="overview-rates-cosigner">
          <span className="overview-rates-cosigner__label">
            Cosigner <span className="overview-rates-cosigner__required">*</span>
          </span>
          <div className="overview-rates-cosigner__options">
            {COSIGNER_OPTIONS.map((opt) => (
              <label key={String(opt.value)} className="overview-rates-cosigner__option">
                <input
                  type="radio"
                  name="cosigner"
                  value={String(opt.value)}
                  checked={formik.values.cosigner === String(opt.value)}
                  onChange={() => formik.setFieldValue('cosigner', String(opt.value))}
                  onBlur={() => formik.setFieldTouched('cosigner', true)}
                />
                {opt.displayName}
              </label>
            ))}
          </div>
          {formik.touched.cosigner && formik.errors.cosigner && (
            <div className="overview-rates-cosigner__error">{formik.errors.cosigner}</div>
          )}
        </div>
      )}

      {showPeerReviewFields && (
        <>
          <p className="overview-subsection-label" style={{ marginTop: 24 }}>Rate Breakdown</p>
          <div className="overview-rates-edit-grid">
            <CurrencyInput
              label="Per Case Rate"
              value={formik.values.perCaseRate}
              onChange={(e) => formik.setFieldValue('perCaseRate', e.target.value)}
            />
            <CurrencyInput
              label="Hourly Rate"
              value={formik.values.hourlyRate}
              onChange={(e) => formik.setFieldValue('hourlyRate', e.target.value)}
            />
            <CurrencyInput
              label="UR/PA Rate"
              value={formik.values.urPaRate}
              onChange={(e) => formik.setFieldValue('urPaRate', e.target.value)}
            />
            {(formik.values.cosigner === '' || isCosignerYes) && (
              <CurrencyInput
                label="Cosign Rate"
                value={formik.values.cosignRate}
                onChange={(e) => formik.setFieldValue('cosignRate', e.target.value)}
              />
            )}
          </div>
        </>
      )}

      <p className="overview-subsection-label" style={{ marginTop: 24 }}>Recent Financial Documents</p>
      <div className="overview-financial-docs">
        {hasIMEType(selectedTypeValue) && (
          <FinancialDocumentRow
            label="Fee Schedule"
            required
            document={docs?.feeSchedule}
            onDownload={docs?.feeSchedule ? () => handleDownload(docs.feeSchedule!) : undefined}
          />
        )}
        <FinancialDocumentRow
          label="Contract"
          required
          document={docs?.contract}
          onDownload={docs?.contract ? () => handleDownload(docs.contract!) : undefined}
        />
        <FinancialDocumentRow
          label="Addendum"
          document={docs?.addendum}
          onDownload={docs?.addendum ? () => handleDownload(docs.addendum!) : undefined}
        />
      </div>

      <div className="overview-rates-actions">
        <TertiaryButton onClick={handleCancel}>Cancel</TertiaryButton>
        <PrimaryButton onClick={() => formik.handleSubmit()} disabled={formik.isSubmitting}>Save</PrimaryButton>
      </div>

      <ConfirmDialog
        dialogVisible={confirmDiscard}
        toggleDialog={() => setConfirmDiscard(false)}
        title="Discard unsaved changes?"
        primaryButtonText="Discard"
        secondaryButtonText="Keep Editing"
        OnPrimaryButtonClick={() => { setConfirmDiscard(false); onCancel(); }}
        OnSecondaryButtonClick={() => setConfirmDiscard(false)}
        isPrimaryDanger
      />
    </div>
  );
};
